<template>
    <div class="free_bracelet_simple">
        <div id="emm_variation_free_bracelet" class="" :data-variation-setting="JSON.stringify(stockVariation)">
            <div v-for="(group, groupIndex) in groupItems" :key="groupIndex" :class="['emm_variation_free_bracelet', `var-${groupIndex}`]">
                <div class="label-variation">
                    {{ group.label }}
                </div>
                <label v-for="(item, index) in group.items" :key="item.id" :for="'var_' + item.id">
                    <span @click="handleRadioChange(item.id, index, groupIndex)" :class="{ disabled: item.disabled, active: item.active }">
                        {{ item.label }}
                    </span>
                </label>
                <div v-if="groupIndex !== 0" class="reset-frebracelet-wrapper" @click="resetSelections">
                    <a class="reset-frebracelet" style="visibility: visible;"><span class="close2">+</span></a>
                </div>
            </div>
        </div>

        <input type="hidden" name="free_bracelet_selected" id="free_bracelet_selected" :value="selectedVariation">
        <input type="hidden" name="is_bracelet_product" id="is_bracelet_product" value="1">
    </div>
</template>

<style lang="scss">
    @import "@/assets/scss/components/product/_bracelet.scss";
</style>

<script setup lang="ts">
    import { computed, onMounted, watch } from 'vue';

    const props = defineProps({
        data: {
            type: Object,
            required: true
        }
    });

    const freeBraceletData = computed(() => props.data);

    const defaultSelection = computed(() => freeBraceletData.value.defaultBracelet);
    const stockVariation = computed(() => freeBraceletData.value.freeBraceletsStock);
    const groupItems = computed(() => {
        const items = freeBraceletData.value.freeBracelet;
        const desiredOrder = ["pa_free-color-charm", "pa_charm-bracelet-length"];

        return [...items].sort((a, b) => {
            const indexA = desiredOrder.indexOf(a.name);
            const indexB = desiredOrder.indexOf(b.name);
            return indexA - indexB;
        });
    });

    const variationSettings = reactive<Record<string, number>>({});
    const selectedAttributes = ref<string[]>([]);

    const selectedVariation = computed(() => {
        return selectedAttributes.value.join('-');
    });

    const isAnyAttributeSelected = computed(() => {
        return selectedAttributes.value.every(attr => attr !== '');
    });

    const emit = defineEmits(['update:freeBraceletSelected', 'update-selection']);

    watch(isAnyAttributeSelected, (newValue) => {
        emit('update:freeBraceletSelected', newValue);
    });

    const initializeDefaultSelection = () => {
        const [defaultLengthId, defaultColorId] = defaultSelection.value.split('-');
        if (stockVariation.value[defaultSelection.value] && stockVariation.value[defaultSelection.value] > 0) {
            setSelection(defaultLengthId, defaultColorId);
        } else {
            for (const [key, stock] of Object.entries(stockVariation.value)) {
                if (stock > 0) {
                    const [lengthId, colorId] = key.split('-');
                    setSelection(lengthId, colorId);
                    break;
                }
            }
        }

        updateAvailability();
    };

    const setSelection = (lengthId: string, colorId: string) => {
        groupItems.value.forEach((group, groupIndex) => {
            group.items.forEach(item => {
                if ((groupIndex === 0 && item.id === Number(lengthId)) || (groupIndex === 1 && item.id === Number(colorId))) {
                    item.active = true;
                    item.disabled = false;
                    selectedAttributes.value[groupIndex] = item.id;
                } else {
                    item.active = false;
                    // Don't set disabled here, it will be handled by updateAvailability
                }
            });
        });
        updateAvailability();
    };

    const updateAvailability = () => {
        const [selectedLength, selectedColor] = selectedAttributes.value;

        groupItems.value.forEach((group, groupIndex) => {
            group.items.forEach(item => {
                if (groupIndex === 0) {
                    // For length group
                    item.disabled = !Object.keys(stockVariation.value).some(key =>
                        key.startsWith(`${item.id}-`) &&
                        (!selectedColor || key.endsWith(`-${selectedColor}`)) &&
                        stockVariation.value[key] > 0
                    );
                } else {
                    // For color group
                    item.disabled = !(
                        stockVariation.value[`${selectedLength}-${item.id}`] &&
                        stockVariation.value[`${selectedLength}-${item.id}`] > 0
                    );
                }
                // Ensure that disabled items are not active
                if (item.disabled) {
                    item.active = false;
                }
            });
        });
    };

    const handleRadioChange = (itemId: string, index: number, groupIndex: number) => {
        const currentGroup = groupItems.value[groupIndex];
        currentGroup.items.forEach((item, i) => {
            item.active = i === index;
        });

        selectedAttributes.value[groupIndex] = itemId;
        updateAvailability();

        const isActive = groupItems.value.every(group => group.items.some(item => item.active));
        emit('update-selection', isActive);
    };

    const resetSelections = () => {
        selectedAttributes.value = [];
        groupItems.value.forEach((group) => {
            group.items.forEach((item) => {
                item.disabled = false;
                item.active = false;
            });
        });
        const activeGroups = groupItems.value.filter(group =>
            group.items.some(item => item.active)
        );
        const isActive = activeGroups.length === groupItems.value.length;
        emit('update-selection', isActive);
    };

    onMounted(() => {
        // extractVariationSettings();
        initializeDefaultSelection();
    });
</script>
